import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, onServerReachability } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusLabel } from "@/lib/status";
import { titleCase } from "@/lib/text";
import { toast } from "sonner";
import { Search as SearchIcon, X, Filter, StickyNote, SlidersHorizontal, ShieldAlert, ChevronDown, Camera } from "lucide-react";
import { ScanField } from "@/components/ScanField";
import { normalizeScan } from "@/lib/scan";
import { scanAccept } from "@/lib/scanFeedback";
import { SynonymSuggestButton } from "@/components/search/SynonymSuggest";
import { PartTapFilters } from "@/components/search/PartTapFilters";

export const ANY = "__any";
export const KEYS = ["q", "category_id", "part_type_id", "make", "model", "year_min", "year_max", "grade", "brand", "engine", "status",
  "price", "where", "main_photo", "verified"];
// S5: a model belongs to its make; the source engine and the part type are filters of their own
const CHILDREN = { make: ["model"] };

// Data-quality triage buckets (All Parts). Order = display order.
export const DQ_DEFS = [
  ["needs_putaway", "Needs put-away"],
  ["no_location", "No location"],
  ["not_on_shelf", "Not on a shelf (no specific spot)"],
  ["no_photos", "No photos"],
  ["no_value", "No value"],
  ["missing_make", "Missing make/model"],
  ["missing_category", "Missing category/type"],
  ["ai_unverified", "AI-unverified"],
  ["unknown_unverified", "Unknown / unverified"],
];

// One shared search/filter engine for both Find a Part (lookup) and All Parts (management).
// defaultStatus: seed the sell search to sellable-only (a Service Writer's normal lookup must
// NOT surface gone/sold parts as if sellable). allStates: the DELIBERATE "show gone parts"
// escape hatch (include_archived) — provenance stays reachable on purpose, never by default.
export function useSearchEngine({ scope = "parts", pageSize = 50, withDq = false, defaultStatus = "", urlBacked = true, box = "", withCounts = false } = {}) {
  const [urlSp, setUrlSp] = useSearchParams();
  const [localSp, setLocalSp] = useState(() => new URLSearchParams());
  // ONE engine, two host contexts: URL-backed for full-page surfaces (Find a Part, All Parts,
  // Vehicles list — resumable/bookmarkable), local-state for embedded tools (Search-Trucks drawer
  // on the parts page, where hijacking the URL would collide with the parts search). Identical logic.
  const sp = urlBacked ? urlSp : localSp;
  const setSp = urlBacked ? setUrlSp : ((val) => setLocalSp(new URLSearchParams(val)));
  const [facets, setFacets] = useState({ makes: [], models: [], engines: [], brands: [], years: [], grades: [], categories: [], part_types: [] });
  const [statuses, setStatuses] = useState([]);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);       // a REFUSED search — never shown behind stale rows
  const [loading, setLoading] = useState(false);
  const [dqCounts, setDqCounts] = useState(null);
  // S5: the filters' counts come in their OWN request, sent once the rows are showing, so they never hold results up
  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const countsSeq = useRef(0);
  const didInit = useRef(false);
  const failedOffline = useRef(false);   // a run that never reached the server

  const f = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, sp.get(k) || ""])), [sp]);
  const dqFlags = useMemo(() => (sp.get("dq") || "").split(",").filter(Boolean), [sp]);
  const activeKeys = KEYS.filter((k) => f[k]);
  const searchNotes = sp.get("notes") === "1";
  const allStates = sp.get("all_states") === "1";

  // Seed the sell search to sellable-only on first mount (only if the caller opted in via
  // defaultStatus and the URL doesn't already carry a status / all-states choice).
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (defaultStatus && !sp.get("status") && !sp.get("all_states")) {
      const n = new URLSearchParams(sp); n.set("status", defaultStatus); setSp(n, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAllStates = (on) => {
    const n = new URLSearchParams(sp);
    if (on) { n.set("all_states", "1"); n.delete("status"); }   // deliberate: show every state incl. gone
    else { n.delete("all_states"); if (defaultStatus) n.set("status", defaultStatus); }
    setSp(n, { replace: true });
  };

  const setF = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
      (CHILDREN[k] || []).forEach((c) => next.delete(c));
    });
    setSp(next, { replace: true });
  };
  const clearAll = () => setSp(new URLSearchParams(), { replace: true });
  const setSearchNotes = (on) => { const n = new URLSearchParams(sp); if (on) n.set("notes", "1"); else n.delete("notes"); setSp(n, { replace: true }); };
  const toggleDq = (flag) => {
    const cur = new Set(dqFlags); cur.has(flag) ? cur.delete(flag) : cur.add(flag);
    const n = new URLSearchParams(sp); const v = [...cur].join(",");
    if (v) n.set("dq", v); else n.delete("dq"); setSp(n, { replace: true });
  };
  const clearDq = () => { const n = new URLSearchParams(sp); n.delete("dq"); setSp(n, { replace: true }); };

  useEffect(() => { if (scope === "trucks") return; api.get("/inventory/constants").then(({ data }) => setStatuses(data.part_statuses || [])).catch(() => {}); }, [scope]);
  useEffect(() => {
    if (scope === "trucks") return;
    api.get("/search/facets", { params: { make: f.make, model: f.model, category_id: f.category_id } })
      .then(({ data }) => setFacets(data)).catch(() => {});
  }, [f.make, f.model, f.category_id, scope]);

  // The /search params for these filters — the page's own search, and the tablet drawer's counts for its copy (S5)
  const paramsFor = (fx, extra = {}) => {
    const params = { scope, page_size: pageSize };
    if (box) params.box = box;
    KEYS.forEach((k) => { if (fx[k]) params[k] = fx[k]; });
    // never a dead end (S3): on nothing found the server also says "did you mean" and what other states hold
    if (fx.q) params.hints = 1;
    if (searchNotes) params.search_notes = 1;
    if (allStates) params.include_archived = 1;
    if (withDq && dqFlags.length) params.dq = dqFlags.join(",");
    return { ...params, ...extra };
  };

  const run = useCallback(async () => {
    setLoading(true);
    try {
      if (scope === "trucks") {
        // Trucks ride the SAME engine/contract but hit the index-backed /truck-search
        // endpoint (different data model — frontend-contract sharing, not a merged query).
        const params = { page_size: pageSize };
        if (f.q) { params.q = f.q; params.hints = 1; }
        if (box) params.box = box;
        Object.assign(params, truckParams((k) => sp.get(k)));
        const { data } = await api.get("/truck-search", { params });
        setErr(null);
        // `for_q` stamps WHICH query produced these rows, so a page can never attribute a
        // stale result set to a query that never reached the server (NG-2).
        //
        // The whole response is passed through DELIBERATELY. This used to whitelist six fields,
        // which silently swallowed every new signal the server added (capture_gap and the
        // synthetic markers both went missing on the Vehicles page and the Search Trucks drawer
        // with no error anywhere) — a frontend that goes deaf as the backend gets more honest.
        setRes({ ...data, for_q: f.q || "" });
      } else {
        const { data } = await api.get("/search", { params: paramsFor(f) });
        setErr(null);
        // same stamp as the trucks branch: WHICH query these rows belong to, so a consumer can
        // never read a settled count against a query that hasn't run yet (the on-donor band's
        // opening rule depends on the shelf count being the shelf count for THIS query).
        setRes({ ...data, for_q: f.q || "" });
        if (withCounts) {
          // every filter option's count for this same search — no box and no hints, so it is never a second record
          const seq = ++countsSeq.current;
          setCountsLoading(true);
          api.get("/search", { params: paramsFor(f, { counts_only: 1, page_size: 1, box: undefined, hints: undefined }) })
            .then(({ data: c }) => { if (seq === countsSeq.current) setCounts(c.filter_counts || null); })
            .catch(() => {})
            .finally(() => { if (seq === countsSeq.current) setCountsLoading(false); });
        }
      }
    } catch (error) {
      // A refused search shows NOTHING, not the previous page of results: rows left on screen
      // under a filter the server rejected are exactly the "68 of 68 while a filter is applied"
      // lie (NG-2/NG-3). The reason is surfaced inline AND as a toast.
      const msg = formatApiErrorDetail(error.response?.data?.detail);
      setErr(error.serverUnreachable ? null : msg);
      if (error.serverUnreachable) failedOffline.current = true;
      if (error.response) {
        setRes(scope === "trucks" ? { items: [], total: 0, page: 1, page_size: pageSize } : { parts: [], parts_total: 0 });
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, searchNotes, allStates, scope, pageSize, withDq, dqFlags, sp, box, withCounts]);

  useEffect(() => {
    const delay = didInit.current ? 300 : 0;
    didInit.current = true;
    const t = setTimeout(run, delay);
    return () => clearTimeout(t);
  }, [run]);

  // A search that never reached the server is re-run the MOMENT the server is back, so the page
  // stops sitting on rows from an older query (the honest-but-stale state) without being retyped.
  useEffect(() => onServerReachability((ok) => {
    if (ok && failedOffline.current) { failedOffline.current = false; run(); }
  }), [run]);

  useEffect(() => {
    if (!withDq) return;
    const params = {};
    ["status", "category_id", "brand", "year", "make", "grade", "model", "engine", "part_type_id", "year_min", "year_max"].forEach((k) => { if (f[k]) params[k] = f[k]; });
    api.get("/search/dq-counts", { params }).then(({ data }) => setDqCounts(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withDq, f.status, f.category_id, f.brand, f.year, f.make, f.grade, f.model, f.engine, f.part_type_id, f.year_min, f.year_max]);

  // Which result was opened (1st, 2nd …) goes on the search record row — never who opened it.
  const reportOpened = useCallback((item) => {
    const list = res?.parts || res?.items || [];
    const i = list.findIndex((x) => x.id === item?.id);
    const page = Number(res?.page || 1);
    if (res?.search_record_id && i >= 0) reportSearchOpened(res.search_record_id, (page - 1) * pageSize + i + 1);
  }, [res, pageSize]);

  return { sp, setSp, f, setF, clearAll, activeKeys, searchNotes, setSearchNotes, allStates, setAllStates, facets, statuses, res, err, loading, run, dqFlags, toggleDq, clearDq, dqCounts, reportOpened, paramsFor, counts, countsLoading };
}

// The Vehicles filters as /truck-search params, read through `get` — the page's own search and the drawer's copy.
export const TRUCK_PARAM_KEYS = ["category", "status", "truck_type", "placement", "sector", "needs_info", "tag_print", "sheet", "photos", "capture", "condition", "presence", "vin_flag", "decode_flag", "dataplate", "make", "model", "engine", "transmission", "arrived", "year_from", "year_to", "order", "page", "gone", "role"];
export function truckParams(get) {
  const params = {};
  TRUCK_PARAM_KEYS.forEach((k) => { const v = get(k); if (v && v !== "all" && v !== "false") params[k] = v === true ? "1" : v; });
  const tf = get("truck_filters");
  if (tf) {
    try {
      const arr = JSON.parse(tf).filter((x) => x && x.value !== "" && x.value != null);
      if (arr.length) params.filters = JSON.stringify(arr);
    } catch { /* ignore malformed */ }
  }
  return params;
}

export function reportSearchOpened(id, rank) {
  if (id && rank > 0) api.post("/search-record/opened", { id, rank }).catch(() => {});
}

// ONE SEARCH BOX FOR EVERY SURFACE THAT REMEMBERS WHERE YOU WERE.
//
// HIS BUG, 2026-10-13: *"Typing 'allison' into the Vehicles search: when I get to 'all', the box
// WIPES CLEAN. And I can't click into the middle of the text to edit it — the cursor jumps back to
// the end every time I type."*
//
// TWO CAUSES, BOTH REAL:
//  1. The Vehicles box was a CONTROLLED input reading its value back out of the URL on every
//     keystroke. The URL is updated through the router, which does not hand the new value back
//     inside the same keystroke, so React re-applied the PREVIOUS string to the box and the
//     browser put the caret at the end — every character, which is why the middle of a word could
//     not be edited.
//  2. "all" is the dropdown filters' word for "no filter", and free text went through the same
//     writer — so `q=all` was DELETED from the URL and the box read back empty. "allison" wiped
//     itself at the third letter.
//
// So the box types LOCALLY (uncontrolled, the DOM owns the caret), reports after a short pause,
// and only accepts an outside value when the crew is not typing in it — or when something cleared
// it on purpose. Each box owns its own timer; they used to share one global, so two boxes on one
// screen cancelled each other's searches.
//
// S5 (his rule, 27 Oct): a search runs on ENTER or the SEARCH button — never while typing. A scan gun types and
// presses Enter, so a scan still lands at once. `live` keeps the old type-and-pause reporting for a box that needs it.
export function RememberedSearchInput({ testid, value, onChange, delay = 350, live = false, ...rest }) {
  const ref = useRef(null);
  const timer = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = value || "";
    if (el.value !== next && (document.activeElement !== el || !next)) {
      // AN OUTSIDE CHANGE BEATS A KEYSTROKE STILL IN FLIGHT. Without this, clicking the X while
      // the last letter was still settling let the pending keystroke put the search straight back
      // — the box cleared and then refilled itself, which is the same "it rewrote itself under me"
      // complaint in a different coat.
      clearTimeout(timer.current);
      el.value = next;
    }
  }, [value]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <Input data-testid={testid} ref={ref} defaultValue={value || ""} {...rest}
      onChange={(e) => {
        if (!live) return;
        const v = e.target.value;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => { timer.current = null; onChange(v); }, delay);
      }}
      onKeyDown={(e) => {
        // ENTER HANDS OVER WHAT IS TYPED NOW (Search S3). A scan gun types and presses Enter in one go; left to the
        // pause, the late report landed after Enter had already opened the item and pulled the screen back.
        if (e.key === "Enter" && (!live || timer.current)) { clearTimeout(timer.current); timer.current = null; onChange(e.currentTarget.value); }
        rest.onKeyDown?.(e);
      }} />
  );
}

// THE SEARCH BUTTON beside a box (S5): hands over what is in the box now, exactly like Enter.
export function SearchGo({ testid, onGo, size = "h-12", label = "Search" }) {
  return (
    <button type="button" data-testid={`${testid}-go`} aria-label="Search"
      onClick={() => onGo(document.querySelector(`[data-testid="${testid}"]`)?.value || "")}
      className={`shrink-0 ${size} rounded-lg bg-[#B01E28] hover:bg-[#8B1820] px-4 text-sm font-bold text-white inline-flex items-center gap-1.5 transition-colors`}>
      <SearchIcon className="w-4 h-4" /><span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// A CAMERA BESIDE A SEARCH BOX — his sweep, 2026-10-13: *"CAMERA SCAN EVERYWHERE — every box,
// same scanner, camera on by default."* A search box that finds a thing by its printed label (a
// part tag, a truck VIN plate, a shelf sticker) gets the lens too, so nobody types a ten-character
// code with gloves on. It is the SAME scanner (`ScanField`) and it drops the decoded code straight
// into the search, which is why it is a sibling button and not a second kind of input: the
// remembered typing behaviour of the box above is untouched.
export function SearchCameraButton({ testid, onScan, size = "h-10" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button type="button" data-testid={`${testid}-camera`} onClick={() => setOpen((o) => !o)}
        title={open ? "Close the camera" : "Scan a label with the camera"}
        aria-label={open ? "Close the camera" : "Scan a label with the camera"}
        className={`${size === "h-12" ? "w-12" : "w-11"} ${size} rounded-lg border flex items-center justify-center transition-colors ${
          open ? "border-[#B01E28] bg-[#B01E28] text-white"
               : "border-slate-200 bg-white text-slate-600 hover:border-[#E0781A] hover:text-[#E0781A]"}`}>
        {open ? <X className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
      </button>
      {open && (
        <div data-testid={`${testid}-camera-panel`}
          className="absolute right-0 top-full mt-2 z-30 w-[22rem] max-w-[86vw] rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <ScanField testid={`${testid}-cam`} onScan={(code) => { const c = normalizeScan(code); scanAccept(c); onScan(c); setOpen(false); }}
            autoStartCamera autoFocus={false} uppercase placeholder="…or type the code here" submitLabel="Search this" />
        </div>
      )}
    </div>
  );
}

// ↑/↓ move · Enter opens · C carts — never while typing in a field.
export function useResultKeyboard({ items, onOpen, onCart }) {
  const [selIdx, setSelIdx] = useState(-1);
  useEffect(() => { setSelIdx(-1); }, [items]);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || t?.isContentEditable
        || (t?.getAttribute && t.getAttribute("role") === "combobox")
        || (t?.closest && t.closest('[role="dialog"]'));
      if (typing || !items.length) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setSelIdx((i) => Math.min((i < 0 ? -1 : i) + 1, items.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelIdx((i) => Math.max((i < 0 ? 0 : i) - 1, 0)); }
      else if (e.key === "Enter" && selIdx >= 0 && items[selIdx]) { onOpen && onOpen(items[selIdx]); }
      else if ((e.key === "c" || e.key === "C") && onCart && selIdx >= 0 && items[selIdx]) { e.preventDefault(); onCart(items[selIdx]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, selIdx, onOpen, onCart]);
  useEffect(() => {
    if (selIdx < 0) return;
    const el = document.querySelector(`[data-row-idx="${selIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selIdx]);
  return { selIdx, setSelIdx };
}

export function VFilter({ testid, label, value, onChange, options, disabled, disabledHint }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</label>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)} disabled={disabled}>
        <SelectTrigger data-testid={testid} title={disabled ? disabledHint : undefined}
          className={`rounded-lg h-9 w-full border px-3 text-sm ${disabled ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400" : value ? "border-[#E0781A] bg-orange-50 text-[#B45309]" : "border-slate-200 text-slate-700"}`}>
          <SelectValue placeholder="Any" />
        </SelectTrigger>
        <SelectContent className="rounded-lg max-h-72">
          <SelectItem value={ANY}>Any</SelectItem>
          {options.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function YearSelect({ testid, value, onChange, years, placeholder }) {
  return (
    <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
      <SelectTrigger data-testid={testid} className={`rounded-lg h-9 w-full border border-slate-200 px-3 text-sm ${value ? "text-[#B45309] border-[#E0781A] bg-orange-50" : "text-slate-500"}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-lg max-h-72">
        <SelectItem value={ANY}>{placeholder}</SelectItem>
        {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// The reusable filter rail: search + (optional) data-quality triage + all filters + active chips.
export function FilterRail({ engine, pfx = "", showDq = false, collapsibleFilters = false, withSearch = true, tapFilters = true, searchPlaceholder = "What is it? e.g. fuel pump, alternator, OEM #…" }) {
  const { f, setF, clearAll, activeKeys, searchNotes, setSearchNotes, facets, run, dqFlags, toggleDq, clearDq, dqCounts, counts, countsLoading } = engine;
  const [filtersExpanded, setFiltersExpanded] = useState(!collapsibleFilters);
  const named = (list, id) => (list || []).find((c) => (c.value ?? c.id) === id)?.label || (list || []).find((c) => c.id === id)?.name || "";
  const whereName = (id) => {
    for (const p of counts?.where?.places || []) {
      if (p.value === id) return p.label;
      const z = (p.zones || []).find((x) => x.value === id);
      if (z) return `${p.label} · ${z.label}`;
    }
    return "Where it is";
  };
  const chipLabel = (k) => {
    const v = f[k];
    switch (k) {
      case "q": return `\u201C${v}\u201D`;
      case "category_id": return titleCase(named(counts?.category, v) || named(facets.categories, v)) || "Category";
      case "part_type_id": return titleCase(named(counts?.part_type, v) || named(facets.part_types, v)) || "Part type";
      case "make": case "model": case "brand": return titleCase(v);
      case "engine": return `Engine: ${String(v).toUpperCase()}`;
      case "grade": return `Grade ${v}`;
      case "status": return statusLabel(v);
      case "year_min": return f.year_max ? null : `${v}+`;
      case "year_max": return f.year_min ? `${f.year_min}\u2013${v}` : `up to ${v}`;
      case "price": return (counts?.price || []).find((o) => o.value === v)?.label || "Price";
      case "where": return whereName(v);
      case "main_photo": return v === "yes" ? "Has a main photo" : "No main photo picked";
      case "verified": return v === "yes" ? "Verified" : "Values still to check";
      default: return v;
    }
  };
  const summaryChips = activeKeys.map((k) => ({ k, label: chipLabel(k) })).filter((c) => c.label);

  return (
    <div className="space-y-4" data-testid={`${pfx}filter-panel`}>
      {withSearch && <form onSubmit={(e) => { e.preventDefault(); run(); }}>
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <RememberedSearchInput testid={`${pfx}search-input`} placeholder={searchPlaceholder}
              value={f.q} onChange={(v) => setF({ q: v })}
              className="rounded-lg border-slate-200 pl-9 h-11 text-base" />
            {f.q && <button type="button" data-testid={`${pfx}search-clear`} onClick={() => setF({ q: "" })} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
          </div>
          <SearchGo testid={`${pfx}search-input`} onGo={(v) => setF({ q: v })} size="h-11" />
          <SearchCameraButton testid={`${pfx}search-input`} onScan={(v) => setF({ q: v })} size="h-11" />
        </div>
        <button type="button" data-testid={`${pfx}toggle-search-notes`} onClick={() => setSearchNotes(!searchNotes)}
          className={`mt-2 inline-flex items-center gap-1.5 rounded-full h-7 px-3 text-[11px] font-medium border transition-colors ${searchNotes ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
          <StickyNote className="w-3.5 h-3.5" />Also search notes {searchNotes ? "· ON" : ""}
        </button>
        <SynonymSuggestButton testid={`${pfx}suggest-synonym`} word={f.q || ""}
          className="mt-2 ml-1.5 inline-flex items-center gap-1.5 rounded-full h-7 px-3 text-[11px] font-medium border border-slate-200 text-slate-500 hover:border-slate-400 transition-colors" />
      </form>}

      {showDq && (
        <div data-testid={`${pfx}dq-panel`} className="border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-[#B01E28]" />Data-quality triage</span>
            {dqFlags.length > 0 && <button data-testid={`${pfx}dq-clear`} onClick={clearDq} className="text-[11px] font-mono text-slate-400 hover:text-[#B01E28] underline">clear</button>}
          </div>
          <div className="flex flex-col gap-1">
            {DQ_DEFS.map(([flag, label]) => {
              const on = dqFlags.includes(flag);
              const n = dqCounts?.[flag];
              return (
                <button key={flag} data-testid={`${pfx}dq-${flag}`} onClick={() => toggleDq(flag)}
                  className={`w-full inline-flex items-center justify-between gap-2 rounded-lg h-8 px-2.5 text-[11px] font-medium border transition-colors ${on ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  <span>{label}</span>
                  {n != null && <span className={`font-mono text-[10px] rounded px-1.5 py-0.5 ${on ? "bg-[#B01E28] text-white" : n > 0 ? "bg-slate-100 text-slate-500" : "bg-slate-50 text-slate-300"}`}>{n}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tapFilters && <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2.5">
          {collapsibleFilters ? (
            <button type="button" data-testid={`${pfx}filter-toggle`} onClick={() => setFiltersExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-700 transition-colors">
              <SlidersHorizontal className="w-3.5 h-3.5" />Narrow it down
              {activeKeys.length > 0 && <span className="font-mono text-[10px] rounded px-1 bg-orange-100 text-[#B45309]">{activeKeys.length}</span>}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filtersExpanded ? "rotate-180" : ""}`} />
            </button>
          ) : (
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" />Narrow it down</span>
          )}
          {activeKeys.length > 0 && <button data-testid={`${pfx}filter-clear`} onClick={clearAll} className="text-[11px] font-mono text-slate-400 hover:text-[#B01E28] underline">clear all</button>}
        </div>
        {/* WHAT IS ON, AT THE TOP (S5): tap a chip's × to drop it, or "clear all" */}
        {summaryChips.length > 0 && (
          <div data-testid={`${pfx}filter-summary`} className="mb-3 flex flex-wrap items-center gap-1.5 text-sm">
            <Filter className="w-3.5 h-3.5 text-slate-400" /><span className="text-slate-500 text-xs">Showing:</span>
            {summaryChips.map(({ k, label }) => (
              <button key={k} data-testid={`${pfx}summary-chip-${k}`} onClick={() => setF({ [k]: "", ...(k === "year_max" ? { year_min: "" } : {}) })}
                className="inline-flex items-center gap-1 min-h-[40px] rounded-full bg-orange-50 border border-orange-200 text-[#B45309] text-sm font-medium px-3 hover:bg-orange-100 transition-colors">
                {label}<X className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        )}
        {filtersExpanded && <PartTapFilters values={f} counts={counts} onPick={setF} pfx={pfx} stale={countsLoading} />}
      </div>}
    </div>
  );
}
